'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { FieldError, fieldErrorClass } from '@/components/ui/field-error';
import { logger } from '@/lib/logger';

/**
 * Imperative, promise-based text prompt — the app-modal replacement for
 * `window.prompt()`. Sibling of `confirmDialog()` / `alertDialog()`.
 *
 *   const name = await promptDialog({
 *     title: 'Name this access review',
 *     message: 'Snapshots all current role grants.',
 *     defaultValue: 'Access review 2026-09-05',
 *     required: true,
 *   });
 *   if (name === null) return; // cancelled
 *
 * Resolves the trimmed string on submit, `null` on cancel / Escape / backdrop.
 * Mount <PromptDialogHost /> exactly once near the app root (layout.tsx).
 * Never falls back to the native prompt — that chrome is what this replaces.
 */
export interface PromptOptions {
  title: string;
  message?: string;
  defaultValue?: string;
  placeholder?: string;
  label?: string;
  confirmText?: string;
  cancelText?: string;
  /** Empty submit is rejected inline with this message (default "This field is required."). */
  required?: boolean;
  requiredMessage?: string;
  /** Extra validation: return a message to block submit, or null/undefined to accept. */
  validate?: (value: string) => string | null | undefined;
}

type Resolver = (value: string | null) => void;
let _open: ((opts: PromptOptions) => Promise<string | null>) | null = null;

export function promptDialog(opts: PromptOptions): Promise<string | null> {
  if (_open) return _open(opts);
  if (typeof console !== 'undefined') logger.warn('[promptDialog] host not mounted:', opts.title);
  return Promise.resolve(null);
}

export function PromptDialogHost() {
  const [opts, setOpts] = useState<PromptOptions | null>(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const resolverRef = useRef<Resolver | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    _open = (o: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        resolverRef.current = resolve;
        setValue(o.defaultValue ?? '');
        setError(null);
        setOpts(o);
      });
    return () => { _open = null; };
  }, []);

  useEffect(() => {
    if (!opts) return;
    const t = setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 60);
    return () => clearTimeout(t);
  }, [opts]);

  const settle = useCallback((result: string | null) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpts(null);
    resolve?.(result);
  }, []);

  const submit = useCallback(() => {
    if (!opts) return;
    const v = value.trim();
    if (opts.required !== false && !v) {
      setError(opts.requiredMessage || 'This field is required.');
      return;
    }
    const msg = opts.validate?.(v);
    if (msg) { setError(msg); return; }
    settle(v);
  }, [opts, value, settle]);

  if (!opts) return null;

  return (
    <AlertDialog open onOpenChange={(next) => { if (!next) settle(null); }}>
      <AlertDialogContent className="max-w-md rounded-2xl border-uzo-line bg-uzo-surface p-0 overflow-hidden">
        <form
          className="p-6"
          onSubmit={(e) => { e.preventDefault(); submit(); }}
        >
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-uzo-accent/20">
              <MessageSquare className="h-5 w-5 text-uzo-accent dark:text-[#8eabf5]" />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <AlertDialogTitle className="text-[15px] font-semibold text-uzo-ink">{opts.title}</AlertDialogTitle>
              {opts.message ? (
                <AlertDialogDescription className="mt-1.5 text-sm leading-relaxed text-uzo-mut">{opts.message}</AlertDialogDescription>
              ) : (
                <AlertDialogDescription className="sr-only">Enter a value</AlertDialogDescription>
              )}
              <div className="mt-4">
                {opts.label && <label className="mb-1.5 block text-[13px] font-medium text-uzo-ink2">{opts.label}</label>}
                <input
                  ref={inputRef}
                  type="text"
                  value={value}
                  placeholder={opts.placeholder}
                  onChange={(e) => { setError(null); setValue(e.target.value); }}
                  aria-invalid={!!error || undefined}
                  className={`w-full rounded-lg border border-uzo-line bg-uzo-surface px-3 py-2 text-sm text-uzo-ink placeholder:text-uzo-faint focus:outline-none focus:ring-2 focus:ring-uzo-accent ${fieldErrorClass(error)}`}
                />
                <FieldError message={error} />
              </div>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={() => settle(null)}
              className="rounded-lg border border-uzo-line bg-uzo-surface px-4 py-2 text-sm font-medium text-uzo-ink2 transition-colors hover:bg-uzo-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-uzo-accent"
            >
              {opts.cancelText || 'Cancel'}
            </button>
            <button
              type="submit"
              className="rounded-lg bg-uzo-accent px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-uzo-accent"
            >
              {opts.confirmText || 'OK'}
            </button>
          </div>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
